import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@lib/api";
import { API_ROUTES } from "../constants";
import { ArticleLikeCountData, QUERY_KEY } from "./useGetArticleLikeCount";

interface ArticleLikePayload {
  articleId: number;
}

export const useToggleArticleLike = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ArticleLikePayload) => {
      await apiClient.post(API_ROUTES.articleLike, payload);
    },
    onMutate: async (payload: ArticleLikePayload) => {
      // Cancel any outgoing fetches for the like count query
      await queryClient.cancelQueries({
        queryKey: [QUERY_KEY, payload.articleId],
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<ArticleLikeCountData>([
        QUERY_KEY,
        payload.articleId,
      ]);

      // Optimistically update state
      queryClient.setQueryData<ArticleLikeCountData>(
        [QUERY_KEY, payload.articleId],
        (prevState) => {
          if (!prevState) return prevState;
          return {
            liked: !prevState.liked,
            likeCount: prevState.liked
              ? prevState.likeCount - 1
              : prevState.likeCount + 1,
          };
        }
      );

      return previousData;
    },
    onError: (
      error: unknown,
      payload: ArticleLikePayload,
      context: ArticleLikeCountData | undefined
    ) => {
      console.error("Failed to toggle article like:", error);
      // Rollback to previous state
      if (context) {
        queryClient.setQueryData([QUERY_KEY, payload.articleId], context);
      }
    },
    onSettled: async (_data, _error, payload: ArticleLikePayload) => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, payload.articleId],
      });
    },
  });
};
